import React from "react";
import { Button, Card, Flex, Typography, Spin, message } from "antd"; // Added Spin import
import {
  DndContext,
  closestCenter,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { useDispatch, useSelector } from "react-redux";
import {
  getDimensionPathHierarchy,
  updateDimensionHierarchy,
} from "../../../../../../../store/action/admin/dimensionsEntityActions";
import { getOverallDimensionHierarchyAction } from "../../../../../../../store/action/admin/dimensionsEntityActions";
import HierarchyIcon from "../../../../../../../Theme/CustomIcons/Icons/CustomIcons/HierarchyIcon";
import CalendarTickIcon from "../../../../../../../Theme/CustomIcons/Icons/Users/CalendarTickIcon";
import MessageTextIcon from "../../../../../../../Theme/CustomIcons/Icons/Emails,Messages/MessageTextIcon";
import CustomLoaderIcon from "../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";
import ArrowCircleRight2Icon from "../../../../../../../Theme/CustomIcons/Icons/Arrow/ArrowCircleRight2Icon";

const { Text } = Typography;

// Helper function to transform dimension type for display
const getDisplayDimensionType = (dimensionType) => {
  if (!dimensionType) return "-";
  const normalized = dimensionType.toLowerCase().trim();
  if (normalized === "client") {
    return "Customer";
  }
  // Capitalize first letter of each word
  return dimensionType
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// ======================== Node Card ========================
const NodeCard = ({ node }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightBg = themeDefault?.info?.themeColor === "default";
  const primaryColor = themeDefault?.info?.colorPrimary;

  return (
    <Card
      title={
        <div
          style={{
            fontWeight: 700,
            textAlign: "center",
            paddingBottom: 8,
            borderBottom: themeDefault?.info?.newConfig?.cardborder,
          }}
        >
          {node.title || "-"}
        </div>
      }
      style={{
        width: 450,
        padding: 16,
        textAlign: "left",
      }}
    >
      <Flex align="center" justify="space-between" gap={8}>
        <HierarchyIcon size="small" />
        <Text style={{ fontSize: 12, fontWeight: 400 }}>
          {getDisplayDimensionType(node.dimension_type)}
        </Text>
        <CalendarTickIcon size="small" />
        <Text style={{ fontSize: 12, fontWeight: 400 }}>
          Created on {node?.createdDate || "-"}
        </Text>
      </Flex>

      {/* <Flex
        align="center"
        justify="space-between"
        style={{
          width: "100%",
          marginTop: 8,
          borderTop: themeDefault?.info?.newConfig?.cardborder,
        }}
      >
        <Button
          type="link"
          size="small"
          onClick={() => console.log("Comments Button clicked")}
          style={{
            color: `${isLightBg ? "#000000" : "#ffffff"}`,
            width: "50%",
            border: "none",
            fontSize: 12,
          }}
        >
          <MessageTextIcon size="small" />
          Comments
        </Button>

        <span
          style={{
            display: "inline-block",
            width: "1px",
            height: "20px",
            backgroundColor: isLightBg ? "#ccc" : "#666",
            margin: "0 4px",
          }}
        />

        <Button
          type="link"
          size="small"
          style={{
            border: "none",
            color: `${isLightBg ? "#000000" : "#ffffff"}`,
            width: "50%",
            fontSize: 12,
          }}
        >
          <ArrowCircleRight2Icon size="small" />
          Details
        </Button>
      </Flex> */}
    </Card>
  );
};

// ======================== DnD Wrappers ========================
const DraggableWrapper = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
  });
  const style = isDragging ? { opacity: 0.5 } : undefined;

  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={style}>
      {children}
    </div>
  );
};

const DroppableWrapper = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const themeDefault = useSelector((state) => state.themeDefault);
  const primaryColor = themeDefault?.info?.colorPrimary;

  const style = isOver
    ? { outline: `2px dashed ${primaryColor}`, borderRadius: 12 }
    : undefined;

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
};

const transformApiDataToTree = (apiData) => {
  const data = apiData?.data || apiData;

  if (!data) {
    return null;
  }

  const { dimension, entity } = data;
  console.log("data", data);
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const transformDimensionNode = (dimNode) => {
    if (!dimNode) return null;

    return {
      id: dimNode.id || dimNode._id || "-",
      title: dimNode.setting?.name || dimNode.name || "-",
      createdDate: formatDate(dimNode.created_at || dimNode.createdAt),
      dimension_type: dimNode.dimension_type || "-",
      children: dimNode.children
        ? dimNode.children.map(transformDimensionNode).filter(Boolean)
        : [],
    };
  };

  const rootNode = {
    id: entity?.id || "root",
    title: entity?.name || "-",
    createdDate: entity?.createdOn ? formatDate(entity.createdOn) : "-",
    dimension_type: "Entity",
    children: [],
  };

  if (dimension) {
    const mainDimensionNode = transformDimensionNode(dimension);
    if (mainDimensionNode) {
      rootNode.children.push(mainDimensionNode);
    }
  }

  return rootNode;
};

const transformOverallTreeToRoot = (
  treeArray,
  entityTitle = "Entity",
  entity = null
) => {
  if (!Array.isArray(treeArray)) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const transformNode = (node) => {
    if (!node) return null;
    return {
      id: node.id || node._id || "-",
      title: node?.setting?.name || node?.name || "-",
      createdDate: formatDate(node.created_at || node.createdAt),
      dimension_type: node.dimension_type || "-",
      children: Array.isArray(node.children)
        ? node.children.map(transformNode).filter(Boolean)
        : [],
    };
  };

  return {
    id: "root",
    title: entityTitle || "Entity",
    createdDate: entity?.createdOn ? formatDate(entity.createdOn) : "-",
    dimension_type: "Entity",
    children: treeArray.map(transformNode).filter(Boolean),
  };
};

// ======================== Tree Utilities ========================
/**
 * Deep clone a tree node
 */
const cloneNode = (node) => {
  return {
    ...node,
    children: node.children ? node.children.map(cloneNode) : [],
  };
};

const findNodeInfo = (root, targetId, parent = null) => {
  if (root.id === targetId) {
    return {
      parent,
      node: root,
      index: parent
        ? parent.children.findIndex((child) => child.id === targetId)
        : -1,
    };
  }

  if (root.children) {
    for (let i = 0; i < root.children.length; i++) {
      const child = root.children[i];
      const result = findNodeInfo(child, targetId, root);
      if (result) return result;
    }
  }

  return null;
};

/**
 * Remove a node from its parent and return the removed node
 */
const removeNodeFromTree = (root, targetId) => {
  const nodeInfo = findNodeInfo(root, targetId);
  if (!nodeInfo || !nodeInfo.parent)
    return { newTree: root, removedNode: null };

  const newParent = {
    ...nodeInfo.parent,
    children: [...nodeInfo.parent.children],
  };
  const [removedNode] = newParent.children.splice(nodeInfo.index, 1);

  const rebuild = (node) => {
    if (node.id === newParent.id) return newParent;
    if (!node.children) return node;
    return { ...node, children: node.children.map(rebuild) };
  };

  return { newTree: rebuild(root), removedNode };
};

/**
 * Insert a node into a parent at specific index
 */
const insertNodeIntoTree = (root, parentId, node, index) => {
  const parentInfo = findNodeInfo(root, parentId);
  if (!parentInfo) return root;

  const newParent = {
    ...parentInfo.node,
    children: [...parentInfo.node.children],
  };
  newParent.children.splice(index, 0, node);

  const rebuild = (currentNode) => {
    if (currentNode.id === newParent.id) return newParent;
    if (!currentNode.children) return currentNode;
    return { ...currentNode, children: currentNode.children.map(rebuild) };
  };

  return rebuild(root);
};

// ======================== Custom Loader Component ========================
const HierarchyLoader = () => {
  const themeDefault = useSelector((state) => state.themeDefault);

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      style={{
        minHeight: 200,
        padding: 40,
      }}
    >
      <Spin
        size="large"
        tip="Loading hierarchy..."
        style={{
          color: themeDefault?.info?.colorPrimary,
        }}
      />
    </Flex>
  );
};

// ======================== Main Component ========================
const EntityHierarchy = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const setDimensionsPathHierarchyList = useSelector(
    (state) => state.setDimensionsPathHierarchyList
  );

  // Check if data is loading
  const isLoading = setDimensionsPathHierarchyList?.loading;

  const getSingleDimensionList = useSelector(
    (state) => state.getSingleDimensionList
  );
  const derivedDimensionId = getSingleDimensionList?.info?.dimension?._id;
  const singleEntityState = useSelector((state) => state.getSingleEntityList);
  const entityId = singleEntityState?.info?._id;
  const getOverallDimensionHierarchyList = useSelector(
    (state) => state.getOverallDimensionHierarchyList
  );

  const defaultTree = {
    id: "root",
    title: "Loading...",
    createdDate: "-",
    children: [],
  };

  const [tree, setTree] = React.useState(defaultTree);

  const submit = async () => ({ departmentHierarchy: tree });
  React.useImperativeHandle(ref, () => ({ submit }));

  React.useEffect(() => {
    if (!derivedDimensionId) return;

    (async () => {
      try {
        await dispatch(getDimensionPathHierarchy(derivedDimensionId));
      } catch (err) {
        console.error("getDimensionPathHierarchy failed:", err);
      }
    })();
  }, [dispatch, derivedDimensionId]);

  React.useEffect(() => {
    const overall = getOverallDimensionHierarchyList?.info?.tree;
    if (Array.isArray(overall)) {
      const entityTitle = singleEntityState?.info?.name || "-";
      const entity = getOverallDimensionHierarchyList?.info?.entity || null;
      const transformed = transformOverallTreeToRoot(
        overall,
        entityTitle,
        entity
      );
      if (transformed) setTree(transformed);
      return;
    }

    const apiData = setDimensionsPathHierarchyList?.info;
    if (apiData) {
      const transformedTree = transformApiDataToTree(apiData);
      if (transformedTree) setTree(transformedTree);
    }
  }, [
    getOverallDimensionHierarchyList,
    setDimensionsPathHierarchyList,
    singleEntityState,
  ]);

  // Ensure the overall hierarchy action is dispatched when entityId is available
  React.useEffect(() => {
    if (!entityId) return;
    (async () => {
      try {
        await dispatch(getOverallDimensionHierarchyAction(entityId));
      } catch (err) {
        console.error("getOverallDimensionHierarchyAction failed:", err);
      }
    })();
  }, [dispatch, entityId]);

  const onDragEnd = (event) => {
    const { active, over } = event;

    // Ignore if no valid drop target or dragging over self
    if (!over || active.id === over.id) return;

    const sourceInfo = findNodeInfo(tree, active.id);
    const targetInfo = findNodeInfo(tree, over.id);

    if (!sourceInfo || !targetInfo) return;

    // Prevent moving a node into its own descendant
    const isDescendant = (parentId, childId) => {
      if (parentId === childId) return true;
      const childInfo = findNodeInfo(tree, childId);
      let current = childInfo?.parent;
      while (current) {
        if (current.id === parentId) return true;
        current = findNodeInfo(tree, current.id)?.parent;
      }
      return false;
    };

    if (isDescendant(active.id, over.id)) return;

    // When dropping onto a node, make that node the parent (insert as child)
    // If dropping onto root, parent remains root
    const newParentId = over.id === "root" ? "root" : over.id;
    const insertAsChild = over.id !== "root";

    // Perform the move operation
    const { newTree: treeAfterRemoval, removedNode } = removeNodeFromTree(
      tree,
      active.id
    );
    if (!removedNode) return;

    let newTree;
    if (insertAsChild) {
      // Insert as a child of the target node (append to its children)
      const targetNodeInfo = findNodeInfo(treeAfterRemoval, over.id);
      if (!targetNodeInfo) return;

      const updatedTargetNode = {
        ...targetNodeInfo.node,
        children: [...(targetNodeInfo.node.children || []), removedNode],
      };

      const rebuild = (node) => {
        if (node.id === updatedTargetNode.id) return updatedTargetNode;
        if (!node.children) return node;
        return { ...node, children: node.children.map(rebuild) };
      };
      newTree = rebuild(treeAfterRemoval);
    } else {
      // Insert as sibling of root's children
      const targetIndex = targetInfo.index !== -1 ? targetInfo.index + 1 : 0;
      let finalTargetIndex = targetIndex;
      if (sourceInfo.parent?.id === "root" && sourceInfo.index < targetIndex) {
        finalTargetIndex = targetIndex - 1;
      }
      newTree = insertNodeIntoTree(
        treeAfterRemoval,
        "root",
        removedNode,
        finalTargetIndex
      );
    }
    setTree(newTree);

    // Persist hierarchy change: child (active.id) moved under parent (newParentId)
    (async () => {
      try {
        const parentDimensionId = newParentId === "root" ? null : newParentId;
        await dispatch(updateDimensionHierarchy(active.id, parentDimensionId));
        message.success("Hierarchy updated successfully");
      } catch (err) {
        console.error("updateDimensionHierarchy failed:", err);
      }
    })();
  };

  const renderLevel = (nodes, parentId = null) => {
    if (!nodes || nodes.length === 0) return null;

    return (
      <Flex
        justify="center"
        gap={24}
        align="flex-start"
        wrap="nowrap"
        style={{
          position: "relative",
          minWidth: "fit-content",
          width: "max-content",
        }}
      >
        {nodes.length > 1 && (
          <div
            style={{
              position: "absolute",
              top: "0px",
              left: "50%",
              transform: "translateX(-50%)",
              width: `calc(100%)`,
              height: "2px",
              backgroundColor: "#e0e0e0",
              zIndex: 0,
            }}
          />
        )}

        {nodes.map((n, idx) => (
          <Flex
            key={n.id}
            vertical
            align="center"
            style={{ position: "relative", zIndex: 1 }}
          >
            {/* Vertical line from parent to this child */}
            <div
              style={{
                width: "2px",
                height: "32px",
                backgroundColor: "#e0e0e0",
                marginBottom: "8px",
              }}
            />

            <DroppableWrapper id={n.id}>
              <DraggableWrapper id={n.id}>
                <NodeCard node={n} />
              </DraggableWrapper>
            </DroppableWrapper>

            {n.children?.length > 0 && (
              <div
                style={{
                  width: "2px",
                  height: "32px",
                  backgroundColor: "#e0e0e0",
                  margin: "8px 0",
                }}
              />
            )}

            <Flex
              justify="center"
              style={{
                position: "relative",
                width: "100%",
                minWidth: "fit-content",
              }}
            >
              {renderLevel(n.children, n.id)}
            </Flex>
          </Flex>
        ))}
      </Flex>
    );
  };

  return (
    <Card title="Hierarchy" bordered>
      <Flex vertical gap={12}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Drag to rearrange dimensions.
        </Text>
        {isLoading ? (
          <Flex align="center" justify="center">
            <CustomLoaderIcon />
          </Flex>
        ) : (
          <div
            style={{
              width: "100%",
              overflowX: "auto",
              overflowY: "visible",
              padding: "8px 0",
            }}
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <Flex
                vertical
                align="center"
                justify="center"
                style={{
                  minWidth: "fit-content",
                  width: "max-content",
                }}
              >
                <DroppableWrapper id={tree.id}>
                  <DraggableWrapper id={tree.id}>
                    <NodeCard node={tree} />
                  </DraggableWrapper>
                </DroppableWrapper>

                {tree.children?.length > 0 && (
                  <div
                    style={{
                      width: "2px",
                      height: "32px",
                      backgroundColor: "#e0e0e0",
                      margin: "12px 0",
                    }}
                  />
                )}

                {renderLevel(tree.children, tree.id)}
              </Flex>
            </DndContext>
          </div>
        )}
      </Flex>
    </Card>
  );
});

export default EntityHierarchy;
